import React, { useState } from 'react';
import { TailwindModal } from '~/libs/ui/atoms/TailwindModal';

import type {
	ErpCountryWithPriceTypeResponse,
	ErpPackResponse,
	ErpPackSessionPriceResponse,
	ErpSessionDetailResponse,
} from 'com.cogvio.pricemonitor.api';

import { PriceVolumeGrowthModalRow, PriceVolumeGrowthValues, VolumeGrowthPrice } from './PriceVolumeGrowthModalRow';
import { DEFAULT_DECIMAL } from '~/UI/Input/DecimalInput';
import { Country } from '~/Api/Country';
import { ModalPriceDotLegend } from './ModalPriceDotLegend';
import { ButtonCancel } from './ButtonCancel';
import { ButtonSave } from './ButtonSave';
import { ERPDistributionType, ERPPriceVolumeGrowth } from '~/libs/infrastructure/schemas/erp';
import { humanDateWithTimeFromString } from '~/libs/utils/format';
import { sortCountries } from '../store/sortingContext';

export type PriceVolumeGrowthModalProps = {
	currency: string;
	erpDate: string;
	erpCountriesResult: ErpCountryWithPriceTypeResponse[];
	erpSessionResult: ErpSessionDetailResponse;
	virtualProductPackName: ErpPackResponse;
	loading: boolean;
	onClose: () => void;
	onSubmit: (priceData: VolumeGrowthPrice[], priceUserYoY: ERPPriceVolumeGrowth[]) => void;
};

export function PriceVolumeGrowthModal({
	currency,
	erpDate,
	erpCountriesResult,
	erpSessionResult,
	virtualProductPackName,
	loading,
	onClose,
	onSubmit,
}: PriceVolumeGrowthModalProps) {
	const defaultGrowthVolumeValues = (erpSessionResult.erpSession.countryFigures || []).reduce(
		(aggregator, data) => ({
			...aggregator,
			[data.country.alpha2]: {
				yearlyVolume: Number(data.yearlyVolume).toFixed(DEFAULT_DECIMAL),
				yoyGrowth: (Number(data.yoyGrowth) * 100).toFixed(DEFAULT_DECIMAL),
				price: undefined,
				isUserDefined: false,
				manuallyChanged: false,
				selectedPrice: null,
				distributionType: ERPDistributionType.RETAIL,
				specialStatus: {
					code: 'no-special-status',
					name: 'No special status',
				},
				isEdited: false,
			},
		}),
		{}
	);

	const valuesWithWithSelectedPrices = (erpSessionResult.inputPrices || []).reduce(
		(aggregator: PriceVolumeGrowthValues, data) => ({
			...aggregator,
			[data.country.alpha2]: {
				...(aggregator[data.country.alpha2] || {}),
				selectedPrice: data.selected ? data.id : (aggregator[data.country.alpha2] || {}).selectedPrice || null,
				price: data.price.price.value,
				isUserDefined: (aggregator[data.country.alpha2] || {}).isUserDefined || data.isUserDefined,
				distributionType: data.distributionType as ERPDistributionType,
				isEdited: false,
				specialStatus: data.specialStatus,
			},
		}),
		defaultGrowthVolumeValues
	);

	const pricesByCountry =
		erpSessionResult.erpSession.prices.reduce(
			(aggregator: { [key: string]: ErpPackSessionPriceResponse[] }, price) => ({
				...aggregator,
				[price.country.alpha2]: [...(aggregator[price.country.alpha2] || []), price],
			}),
			{}
		) || {};

	const [editState, setEditState] = useState<PriceVolumeGrowthValues>(valuesWithWithSelectedPrices);
	const disabled = loading;

	const handleSubmit = async () => {
		const inputEntries = Object.entries(editState);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const hasErrors = inputEntries.some(([_, data]) => data.errors?.size);

		if (disabled || hasErrors || !erpSessionResult.erpPack.id) {
			return;
		}

		const priceData = inputEntries
			.map(([country, { price, selectedPrice, isUserDefined, manuallyChanged, distributionType, isEdited }]) => ({
				country,
				price,
				currency,
				selectedPrice,
				isUserDefined,
				manuallyChanged,
				distributionType,
				isEdited,
			}))
			.filter(({ price, distributionType }) => Boolean(price) || Boolean(distributionType));

		const priceUserYoY = Object.entries(editState).map(([countryCode, { yearlyVolume, yoyGrowth }]) => ({
			countryCode,
			yearlyVolume,
			yoyGrowth,
		}));

		onSubmit(priceData, priceUserYoY);
	};

	const isInputValid = Object.values(editState).every(({ errors }) => !errors?.size);

	return (
		<TailwindModal
			footer={
				<>
					<ButtonCancel onClick={onClose}>Close</ButtonCancel>
					<ButtonSave disabled={disabled || !isInputValid} loading={loading}>
						Save &amp; execute model
					</ButtonSave>
				</>
			}
			title='Price input, volume & growth'
			onClose={onClose}
			onSubmit={handleSubmit}
		>
			<>
				<div className='pb-1'>
					<div className='flex items-center'>
						<div className='py-1.5 px-2 rounded font-display font-semibold text-gray-800 text-center bg-gray-100'>
							{humanDateWithTimeFromString(erpDate)
								.split(' ')
								.map((word, index) => (
									<span key={index} className='block text-label leading-tight text-left tabular-nums'>
										{word}
									</span>
								))}
						</div>
						<div className='pl-3'>
							<h1 className='relative -top-0.5 block font-display text-md font-semibold text-cPurple-600 tracking-wider'>
								{virtualProductPackName.brandName}

								<p className='block text-gray-700 font-normal tabular-nums'>
									{virtualProductPackName.virtualProductPackName.split('-')[1]}
								</p>
							</h1>
						</div>
					</div>
					<ModalPriceDotLegend />
				</div>
				<div className='-mx-3'>
					<table cellPadding='0' cellSpacing='0' width='100%'>
						<thead>
							<tr>
								<th className='pl-3 pr-2 py-1.5 text-left font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									Country
								</th>
								<th className='px-2 py-1.5 text-left font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									Price [{currency}]
								</th>
								<th className='px-1 py-1.5 text-center font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									Level
								</th>
								<th className='pl-2 py-1.5 text-left font-display text-labelsmall font-medium text-gray-600 leading-none border-b-2 border-gray-300 w-20'>
									Product
									<br />
									type
								</th>
								<th className='pl-2 py-1.5 text-left font-display text-labelsmall font-medium text-gray-600 leading-none border-b-2 border-gray-300 w-20'>
									Rule
									<br />
									type
								</th>
								<th className='px-2 py-1.5 text-left font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									Yearly volume [Nr.]
								</th>
								<th className='pr-3 pl-2 py-1.5 text-left font-display text-label font-medium text-gray-600 leading-none border-b-2 border-gray-300'>
									YoY growth [%]
								</th>
							</tr>
						</thead>
						<tbody>
							{sortCountries(erpCountriesResult || []).map((country) => (
								<PriceVolumeGrowthModalRow
									key={country.alpha2}
									availablePrices={pricesByCountry[country.alpha2] || []}
									country={new Country(country.alpha2, country.name)}
									priceType={country.priceType}
									values={editState[country.alpha2]}
									isHospitalRelevant={country.isHospitalRelevant}
									onVolumeGrowthChangeRequest={setEditState}
								/>
							))}
						</tbody>
					</table>
				</div>
				<ModalPriceDotLegend />
			</>
		</TailwindModal>
	);
}
